import type { AnimationI } from "tweenly"
import { Box } from "@mui/material"
import Grid from "@mui/material/Grid"
import { memo, useEffect, useMemo, useRef, useState } from "react"
import { selectUndoable } from "../../helpers/selector.helpers"
import useTimelineWheelZoom from "../../hooks/editor/useTimelineWheelZoom"
import { useAppDispatch, useAppSelector } from "../../hooks/useRedux"
import useSliderTime from "../../hooks/useSliderTime"
import { useMasterTimeline } from "../../hooks/useMasterTimeline"
import { useScrollOverflow } from "../../hooks/useScrollOverflow"
import { scrollTimelineTo, useTimelineViewportSource } from "../../hooks/useTimelineViewport"
import { selectAllAnimations } from "../../store/slices/objects.slice/animation/selectAllAnimations"
import { setTimelinePreferencesAsync } from "../../store/slices/timelinePreferences.slice"
import { TIMELINE_ROW, calcTimelineRowWidth } from "../../style/sizing"
import Timeline from "./TimelineNavbar/Timeline"
import TimeStamps from "./TimelineNavbar/Timeline/TimeStamps"
import TimelineControlTools from "./TimelineNavbar/Timeline/TimelineControlTools"
import EdgeFade from "../common/EdgeFade"

/**
 * Just under the sticky ruler and names column (50), over the tween bars. Not above them: the
 * playhead line belongs to the ruler, so its own 60 only counts inside the ruler's 50 — a
 * fade above 50 would cover it. The names column is opaque, so the fades show over the bars.
 */
const TIMELINE_FADE_Z_INDEX = 49

const TimelineNavbar = memo(() => {
    const dispatch = useAppDispatch()
    const timelinePreferences = useAppSelector((state) => state.timelinePreferences.value)
    const timelineZoom: number = timelinePreferences.timelineZoom

    const objects = useAppSelector((state) => selectUndoable(state).objects)
    const animations: AnimationI[] = useMemo(() => {
        return selectAllAnimations(Object.values(objects.value))
    }, [objects])

    //show timestamps only when animation exists, except ticker animation
    const showTimeStamps: boolean = useMemo(() => {
        return animations.filter((animation) => !animation.subTimeline).length > 0
    }, [animations])

    const { masterTimelineDuration } = useMasterTimeline()

    const visibilityByAnimations = animations.length > 0 ? "visible" : "hidden"
    const visibilityByObjects =
        objects.value === null || Object.keys(objects.value).length === 0 ? "hidden" : "visible"
    const disabledByAnimations = animations.length > 0 ? false : true

    const width = 0
    const timelineRowWidth: number = useMemo(
        () => calcTimelineRowWidth(width, masterTimelineDuration, timelineZoom),
        [masterTimelineDuration, timelineZoom]
    )
    const timelineRowAreaWidth: number = TIMELINE_ROW.headingWidth
    const timelineAreaWidth = timelineRowWidth + timelineRowAreaWidth

    const timeStampsRef = useRef<HTMLDivElement>(null)

    // single scroll listener for every timeline cell (see useTimelineViewport)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    useTimelineViewportSource(scrollContainerRef)
    // Cmd/Ctrl + wheel and touchpad pinch, centred on the pointer
    useTimelineWheelZoom(scrollContainerRef)
    // which edges have more timeline past them, for the fades below
    const scrollOverflow = useScrollOverflow(scrollContainerRef)

    const handleFitTimeline = () => {
        const customLineSlider = document.querySelector(
            ".custom-line-slider"
        ) as HTMLDivElement | null

        if (timeStampsRef.current && customLineSlider) {
            scrollTimelineTo(0)

            const newTimelineZoom =
                ((timeStampsRef.current.clientWidth - TIMELINE_ROW.headingWidth) * timelineZoom) /
                customLineSlider.clientWidth

            dispatch(
                setTimelinePreferencesAsync("timelineZoom", Math.round(newTimelineZoom * 100) / 100)
            )
        }
    }

    const timelineRef = useRef<HTMLDivElement>()
    const [thumbHeight, setThumbHeight] = useState<number>(0)
    useEffect(() => {
        if (!timelineRef.current) return

        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.contentRect.height) {
                    setThumbHeight(entry.contentRect.height)
                }
            }
        })

        observer.observe(timelineRef.current)

        return () => observer.disconnect()
    }, [timelineRef])

    const [stableDuration, setStableDuration] = useState(masterTimelineDuration)

    useEffect(() => {
        if (masterTimelineDuration > 0) {
            setStableDuration(masterTimelineDuration)
        }
    }, [masterTimelineDuration])

    const timelineTweensPerSec = timelinePreferences.timelineTweensPerSec

    const timeStepsDensity = useMemo(() => {
        return Math.ceil(stableDuration * timelineTweensPerSec) > 0
            ? Math.ceil(stableDuration * timelineTweensPerSec)
            : 1
    }, [stableDuration, timelineTweensPerSec])

    const sliderTimeProps = useSliderTime()

    const { effectiveTimelineDuration } = useMasterTimeline()

    return (
        <Grid container direction="column" sx={{ height: "100%", paddingBottom: 2 }}>
            <TimelineControlTools
                visibility={visibilityByObjects}
                disabled={disabledByAnimations}
                handleFitTimeline={handleFitTimeline}
                sliderTimeProps={sliderTimeProps}
            />
            {/* the frame the fades are drawn in — the scroll container itself would scroll them */}
            <Box
                sx={{ position: "relative", flex: 1, minHeight: 0, width: "100%", display: "flex" }}
            >
                <Box
                    ref={scrollContainerRef}
                    className="custom-timeline-navbar"
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        width: "100%",
                        // no rubber-band bounce at the ends: it would carry the sticky ruler
                        // away from the fades, which stay put outside the scroller
                        overscrollBehavior: "none",
                        overflow:
                            visibilityByObjects === "hidden" || visibilityByAnimations === "hidden"
                                ? "hidden"
                                : "scroll",
                    }}
                >
                    {showTimeStamps && (
                        <TimeStamps
                            thumbHeight={thumbHeight}
                            ref={timeStampsRef}
                            width={timelineRowWidth}
                            visibility={visibilityByAnimations}
                            timeStepsDensity={timeStepsDensity}
                            duration={masterTimelineDuration}
                            effectiveDuration={effectiveTimelineDuration}
                            handleChange={sliderTimeProps.handleChange}
                            handleClickTimeStamp={sliderTimeProps.handleClickTimeStamp}
                        />
                    )}
                    <Box
                        ref={timelineRef}
                        sx={{
                            width:
                                visibilityByObjects === "hidden"
                                    ? "100%"
                                    : timelineAreaWidth + "px",
                            flex: 1, // to fill remaining vertical space
                            overflowY: "visible", //auto// vertically scrolable
                            overflowX: "visible",
                        }}
                    >
                        <Timeline size={{ width: timelineRowWidth }} />
                    </Box>
                </Box>
                {/**
                 * Edges with more timeline past them fade, like the objects tree and the settings
                 * panels — under the ruler and its playhead (see TIMELINE_FADE_Z_INDEX). The top
                 * one starts under the ruler, the left one after the names, and all of them stop
                 * short of the scrollbars.
                 */}
                {scrollOverflow.top && (
                    <EdgeFade
                        edge="top"
                        zIndex={TIMELINE_FADE_Z_INDEX}
                        inset={{ top: TIMELINE_ROW.height, right: scrollOverflow.scrollbarWidth }}
                    />
                )}
                {scrollOverflow.bottom && (
                    <EdgeFade
                        edge="bottom"
                        zIndex={TIMELINE_FADE_Z_INDEX}
                        inset={{
                            bottom: scrollOverflow.scrollbarHeight,
                            right: scrollOverflow.scrollbarWidth,
                        }}
                    />
                )}
                {scrollOverflow.left && (
                    <EdgeFade
                        edge="left"
                        zIndex={TIMELINE_FADE_Z_INDEX}
                        inset={{
                            left: TIMELINE_ROW.headingWidth,
                            bottom: scrollOverflow.scrollbarHeight,
                        }}
                    />
                )}
                {scrollOverflow.right && (
                    <EdgeFade
                        edge="right"
                        zIndex={TIMELINE_FADE_Z_INDEX}
                        inset={{
                            right: scrollOverflow.scrollbarWidth,
                            bottom: scrollOverflow.scrollbarHeight,
                        }}
                    />
                )}
            </Box>
        </Grid>
    )
})

export default TimelineNavbar
